import { useCallback, useMemo } from 'react';

import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useCurrentEditingMode } from 'src/store/document/selectors';

import { plannedOnly, useIfEditableMixin } from './menu-mixins';

const LINE_SPACING_OPTIONS: [label: string, lineSpacing: number][] = [
  ['Single', 1],
  ['1.15', 1.15],
  ['1.5', 1.5],
  ['Double', 2],
];

export function useLineSpacingMenu(): DeclarativeMenuConfig {
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const editingMode = useCurrentEditingMode();
  const editor = useEditor();
  const ifEditable = useIfEditableMixin();
  const lineSpacing = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.lineSpacing
  );
  const spacingAfter = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.spacingAfter
  );
  const spacingBefore = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.spacingBefore
  );

  const setLineSpacing = useCallback(
    (lineSpacing: number) => () => {
      if (!currentUserId || !editingMode) return;
      AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, { lineSpacing });
      ClientTransforms.focus(editor);
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  const toggleSpacingAfter = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
      spacingAfter: spacingAfter ? 0 : 6,
    });
    ClientTransforms.focus(editor);
  }, [AuthorizedTransforms, currentUserId, editingMode, editor, spacingAfter]);

  const toggleSpacingBefore = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
      spacingBefore: spacingBefore ? 0 : 6,
    });
    ClientTransforms.focus(editor);
  }, [AuthorizedTransforms, currentUserId, editingMode, editor, spacingBefore]);

  return useMemo(
    () => [
      ...LINE_SPACING_OPTIONS.map(([label, newLineSpacing]) => ({
        checked: lineSpacing === newLineSpacing,
        label,
        onClick: setLineSpacing(newLineSpacing),
        ...ifEditable,
      })),
      DECLARATIVE_MENU_SEPARATOR,
      {
        label: `${spacingBefore ? 'Remove' : 'Add'} space before paragraph`,
        onClick: toggleSpacingBefore,
        ...ifEditable,
      },
      {
        label: `${spacingAfter ? 'Remove' : 'Add'} space after paragraph`,
        onClick: toggleSpacingAfter,
        ...ifEditable,
      },
      { label: 'Custom spacing...', ...ifEditable, ...plannedOnly },
    ],
    [
      ifEditable,
      lineSpacing,
      setLineSpacing,
      spacingAfter,
      spacingBefore,
      toggleSpacingAfter,
      toggleSpacingBefore,
    ]
  );
}
